import { TBtn } from "./EditorButtons";
import Icon from "./Icon";
import { useState, useEffect } from "react";
import { Tooltip, Dropdown } from "antd";

export default function EditorTopBar({
  tool,
  zoom,
  isFullscreen,
  saveStatus,
  onClose,
  setTool,
  setZoom,
  closeAllPopups,
  toggleFullscreen,
  downloadImage,
  saveImage,
  clearObjects,
  undo,
  redo,
  history,
  redoStack,
  setShowHelpModal,
  layoutMode,
  setLayoutMode,
}) {
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 1024);
    };
    checkMobile();
    window.addEventListener("resize", checkMobile);
    return () => window.removeEventListener("resize", checkMobile);
  }, []);

  const toggleLayout = () => {
    setLayoutMode(prev => {
      if (prev !== "auto") return "auto";
      return window.innerWidth < 1024 ? "left" : "bottom";
    });
  };

  const groupStyle = {
    display: "flex",
    alignItems: "center",
    gap: 4,
    background: "rgba(22, 22, 26, 0.7)",
    backdropFilter: "blur(12px)",
    border: "1px solid rgba(255, 255, 255, 0.08)",
    borderRadius: 12,
    padding: 6,
    pointerEvents: "auto",
    boxShadow: "0 8px 32px rgba(0,0,0,0.3)",
  };

  const rawBtnStyle = {
    background: "transparent",
    border: "none",
    color: "#ddd",
    borderRadius: 8,
    cursor: "pointer",
    transition: "all 0.15s ease",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    height: 32,
    padding: "0 8px",
    fontWeight: 600,
    fontSize: 12,
  };

  const [showMobileMenu, setShowMobileMenu] = useState(false);

  if (isMobile) {
    const isUndoDimmed = !history || history.length === 0;
    const isRedoDimmed = !redoStack || redoStack.length === 0;
    const undoCount = history?.length || 0;
    const redoCount = redoStack?.length || 0;

    return (
      <>
        <div 
          className="fd-hscroll"
          style={{
            position: "absolute",
            top: 0,
            left: 0,
            right: 0,
            zIndex: 30,
            display: "flex",
            flexDirection: "row",
            alignItems: "center",
            gap: 6,
            background: "rgba(18, 18, 22, 0.90)",
            backdropFilter: "blur(20px) saturate(1.8)",
            borderBottom: "1px solid rgba(255, 255, 255, 0.08)",
            height: "var(--fd-chrome-size, 56px)",
            padding: "0 16px",
            pointerEvents: "auto",
            boxShadow: "0 4px 24px rgba(0, 0, 0, 0.4)",
            overflowX: "auto",
            overflowY: "hidden",
            whiteSpace: "nowrap",
            WebkitOverflowScrolling: "touch",
          }}
        >
          {/* Close Button */}
          {typeof onClose === "function" && (
            <button onClick={onClose} style={{
              background: "rgba(255, 255, 255, 0.05)", border: "none", color: "#ef4444",
              width: 36, height: 36, borderRadius: 10, display: "flex", alignItems: "center", justifyContent: "center",
              cursor: "pointer", outline: "none", flexShrink: 0
            }}>
              <Icon id="close" size={16}/>
            </button>
          )}

          {/* Save Button */}
          <button onClick={saveImage} disabled={saveStatus==="saving"} style={{
            background:"#ffcc00", border:"none", borderRadius:10, width: 36, height: 36,
            cursor:saveStatus==="saving"?"default":"pointer", color:"#000",
            display:"flex", alignItems:"center", justifyContent:"center", opacity:saveStatus==="saving"?0.8:1, transition:"all 0.15s", outline: "none", flexShrink: 0
          }}>
            {saveStatus === "saving" ? (
              <svg width="16" height="16" viewBox="0 0 24 24" style={{ animation: "editor-spin 0.8s linear infinite" }}>
                <circle cx="12" cy="12" r="10" stroke="rgba(0,0,0,0.2)" strokeWidth="3" fill="none" />
                <path d="M12 2a10 10 0 0 1 10 10" stroke="#000" strokeWidth="3" strokeLinecap="round" fill="none" />
              </svg>
            ) : (
              <Icon id="save" size={18}/>
            )}
          </button>

          {/* Save Status */}
          {saveStatus === "saved" && (
            <span style={{fontSize:12,fontWeight:600,color:"#4ade80",flexShrink:0}}>Saved ✓</span>
          )}
          {saveStatus === "fail" && (
            <span style={{fontSize:12,fontWeight:600,color:"#ef4444",flexShrink:0}}>Failed ✗</span>
          )}

          {/* Undo */}
          <button 
            disabled={isUndoDimmed}
            onClick={undo}
            style={{
              background: "rgba(255, 255, 255, 0.05)", border: "none", color: isUndoDimmed ? "#555" : "#ddd",
              width: 36, height: 36, borderRadius: 10, display: "flex", alignItems: "center", justifyContent: "center",
              cursor: isUndoDimmed ? "default" : "pointer", position: "relative", outline: "none", flexShrink: 0
            }}
          >
            <Icon id="undo" size={18}/>
            {undoCount > 0 && (
              <div style={{ position: "absolute", top: -4, right: -4, background: "#ffcc00", color: "#000", fontSize: 9, fontWeight: 800, minWidth: 14, height: 14, padding: "0 3px", borderRadius: 7, display: "flex", alignItems: "center", justifyContent: "center", pointerEvents: "none", boxShadow: "0 1px 3px rgba(0,0,0,0.5)" }}>
                {undoCount}
              </div>
            )}
          </button>

          {/* Redo */}
          <button 
            disabled={isRedoDimmed}
            onClick={redo}
            style={{
              background: "rgba(255, 255, 255, 0.05)", border: "none", color: isRedoDimmed ? "#555" : "#ddd",
              width: 36, height: 36, borderRadius: 10, display: "flex", alignItems: "center", justifyContent: "center",
              cursor: isRedoDimmed ? "default" : "pointer", position: "relative", outline: "none", flexShrink: 0
            }}
          >
            <Icon id="redo" size={18}/>
            {redoCount > 0 && (
              <div style={{ position: "absolute", top: -4, right: -4, background: "#ffcc00", color: "#000", fontSize: 9, fontWeight: 800, minWidth: 14, height: 14, padding: "0 3px", borderRadius: 7, display: "flex", alignItems: "center", justifyContent: "center", pointerEvents: "none", boxShadow: "0 1px 3px rgba(0,0,0,0.5)" }}>
                {redoCount}
              </div>
            )}
          </button>

          {/* Zoom Out */}
          <button onClick={() => setZoom(z => Math.max(z - 0.25, 0.5))} style={{
            background: "rgba(255, 255, 255, 0.05)", border: "none", color: "#ddd",
            width: 36, height: 36, borderRadius: 10, display: "flex", alignItems: "center", justifyContent: "center",
            cursor: "pointer", outline: "none", flexShrink: 0
          }}>
            <Icon id="zoomout" size={18}/>
          </button>

          {/* Zoom Level Reset */}
          <button onClick={() => setZoom(1)} style={{
            background: "transparent", border: "none", color: "#ffcc00",
            fontSize: 12, fontWeight: 700, padding: "0 4px", height: 36, cursor: "pointer", outline: "none", flexShrink: 0
          }}>
            {Math.round(zoom * 100)}%
          </button>

          {/* Zoom In */}
          <button onClick={() => setZoom(z => Math.min(z + 0.25, 4))} style={{
            background: "rgba(255, 255, 255, 0.05)", border: "none", color: "#ddd",
            width: 36, height: 36, borderRadius: 10, display: "flex", alignItems: "center", justifyContent: "center",
            cursor: "pointer", outline: "none", flexShrink: 0
          }}>
            <Icon id="zoomin" size={18}/>
          </button>

          {/* Clear All */}
          <button onClick={clearObjects} style={{
            background: "rgba(255, 255, 255, 0.05)", border: "none", color: "#ef4444",
            width: 36, height: 36, borderRadius: 10, display: "flex", alignItems: "center", justifyContent: "center",
            cursor: "pointer", outline: "none", flexShrink: 0
          }}>
            <Icon id="trash" size={18}/>
          </button>


          {/* 3 Dot More Menu Button */}
          <button 
            onClick={(e) => { e.stopPropagation(); setShowMobileMenu(!showMobileMenu); }} 
            style={{
              background: showMobileMenu ? "rgba(255, 255, 255, 0.15)" : "rgba(255, 255, 255, 0.05)", border: "none", color: "#ddd",
              width: 36, height: 36, borderRadius: 10, display: "flex", alignItems: "center", justifyContent: "center",
              cursor: "pointer", outline: "none", flexShrink: 0, marginRight: 16, transition: "background 0.2s"
            }}>
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none"><circle cx="5" cy="12" r="2" fill="currentColor"/><circle cx="12" cy="12" r="2" fill="currentColor"/><circle cx="19" cy="12" r="2" fill="currentColor"/></svg>
          </button>
        </div>

        {/* Custom Mobile Menu Popup */}
        {showMobileMenu && (
          <>
            <div 
              style={{ position: "fixed", top: 0, left: 0, right: 0, bottom: 0, zIndex: 9999 }} 
              onClick={() => setShowMobileMenu(false)} 
            />
            <div style={{
              position: "absolute", top: "calc(var(--fd-chrome-size, 56px) + 8px)", right: 16, zIndex: 10000,
              background: "rgba(28, 28, 34, 0.95)", backdropFilter: "blur(12px)", border: "1px solid rgba(255, 255, 255, 0.1)",
              borderRadius: 12, padding: 8, display: "flex", flexDirection: "column", gap: 4, boxShadow: "0 8px 32px rgba(0,0,0,0.5)",
              pointerEvents: "auto"
            }}>
              <button 
                onClick={() => { setShowMobileMenu(false); downloadImage(); }} 
                style={{
                  display: 'flex', alignItems: 'center', gap: 10, color: '#fff', fontSize: 14, padding: "8px 16px",
                  background: "transparent", border: "none", cursor: "pointer", borderRadius: 8, outline: "none", textAlign: "left"
                }}>
                <Icon id="download" size={16}/> <span style={{ fontWeight: 500 }}>Download</span>
              </button>
              <button 
                onClick={() => { setShowMobileMenu(false); setShowHelpModal(true); }} 
                style={{
                  display: 'flex', alignItems: 'center', gap: 10, color: '#fff', fontSize: 14, padding: "8px 16px",
                  background: "transparent", border: "none", cursor: "pointer", borderRadius: 8, outline: "none", textAlign: "left"
                }}>
                <span style={{ fontSize: 14, fontWeight: 800, width: 16, textAlign: 'center' }}>?</span> <span style={{ fontWeight: 500 }}>Shortcuts</span>
              </button>
              <button 
                onClick={() => { setShowMobileMenu(false); toggleLayout(); }} 
                style={{
                  display: 'flex', alignItems: 'center', gap: 10, color: layoutMode !== "auto" ? '#ffcc00' : '#fff', fontSize: 14, padding: "8px 16px",
                  background: layoutMode !== "auto" ? "rgba(255, 204, 0, 0.15)" : "transparent", border: "none", cursor: "pointer", borderRadius: 8, outline: "none", textAlign: "left"
                }}>
                <Icon id="layout" size={16}/> <span style={{ fontWeight: 500 }}>{layoutMode === "left" ? "Layout: Left" : "Change Layout"}</span>
              </button>
            </div>
          </>
        )}
      </>
    );
  }

  return (
    <div style={{
      position: "absolute", top: 4, left: layoutMode !== "bottom" ? "calc(var(--fd-chrome-size, 68px) + 16px)" : 16, right: 30, zIndex: 30,
      display: "flex", alignItems: "flex-start", justifyContent: "space-between",
      pointerEvents: "none" // Allow clicks to pass through empty space
    }}>
      {/* ── Left Controls Group ── */}
      <div style={groupStyle}>
        <TBtn title={`Layout: ${layoutMode}`} onClick={toggleLayout} active={layoutMode !== "auto"}>
          <Icon id="layout" size={16}/>
        </TBtn>
        <div style={{ width: 1, height: 16, background: "rgba(255,255,255,0.1)", margin: "0 4px" }} />
        {[["expand","Fullscreen"],["zoomout","Zoom Out"],["zoomval","Reset Zoom"],["zoomin","Zoom In"],["sep",""],["undo","Undo"],["redo","Redo"],["sep",""],["download","Download"],["trash","Delete All"]].map(([ic,title], i)=>{
          if (ic === "sep") return <div key={i} style={{ width: 1, height: 16, background: "rgba(255,255,255,0.1)", margin: "0 4px" }} />;
          
          if (ic === "zoomval") {
            return (
              <Tooltip key="zoomval" title="Reset Zoom to 100%" placement="bottom" color="rgba(15, 15, 18, 0.97)" mouseEnterDelay={0.1}>
                <button onClick={() => setZoom(1)} style={{ ...rawBtnStyle, width: 44 }} onMouseEnter={e => { e.currentTarget.style.background="rgba(255,255,255,0.1)"; e.currentTarget.style.color="#fff"; }} onMouseLeave={e => { e.currentTarget.style.background="transparent"; e.currentTarget.style.color="#ddd"; }}>
                  {Math.round(zoom * 100)}%
                </button>
              </Tooltip>
            );
          }

          const isUndo = ic === "undo";
          const isRedo = ic === "redo";
          const dimmed = (isUndo && (!history || history.length === 0)) || (isRedo && (!redoStack || redoStack.length === 0));
          const count = isUndo ? (history?.length || 0) : isRedo ? (redoStack?.length || 0) : 0;
          return (
            <TBtn key={ic} title={title} active={false} dimmed={dimmed}
              onClick={
                dimmed ? undefined :
                ic==="expand"?toggleFullscreen:
                ic==="zoomin"?()=>setZoom(z=>Math.min(z+0.25,4)):
                ic==="zoomout"?()=>setZoom(z=>Math.max(z-0.25,0.5)):
                ic==="undo"?undo:
                ic==="redo"?redo:
                ic==="download"?downloadImage:
                ic==="trash"?clearObjects:
                undefined
              }>
              <Icon id={ic==="expand"&&isFullscreen?"collapse":ic} size={16}/>
              {(isUndo || isRedo) && count > 0 && (
                <div style={{ position: "absolute", top: -4, right: -4, background: "#ffcc00", color: "#000", fontSize: 9, fontWeight: 800, minWidth: 14, height: 14, padding: "0 3px", borderRadius: 7, display: "flex", alignItems: "center", justifyContent: "center", pointerEvents: "none", boxShadow: "0 2px 4px rgba(0,0,0,0.5)" }}>
                  {count > 99 ? "99+" : count}
                </div>
              )}
            </TBtn>
          );
        })}
      </div>

      {/* ── Right Controls Group ── */}
      <div style={groupStyle}>
        {saveStatus === "saved" && (
          <span style={{fontSize:13,fontWeight:600,color:"#4ade80",padding:"0 8px",letterSpacing:"0.3px"}}>Saved ✓</span>
        )}
        {saveStatus === "fail" && (
          <span style={{fontSize:13,fontWeight:600,color:"#ef4444",padding:"0 8px",letterSpacing:"0.3px"}}>Failed ✗</span>
        )}
        <button onClick={saveImage} disabled={saveStatus==="saving"} style={{background:"#ffcc00",border:"none",borderRadius:8,padding:"0 16px",height:34,fontWeight:700,fontSize:14,cursor:saveStatus==="saving"?"default":"pointer",color:"#000",display:"flex",gap:8,alignItems:"center",opacity:saveStatus==="saving"?0.8:1,transition:"all 0.15s",boxShadow:"0 2px 10px rgba(255,204,0,0.25)"}}>
          {saveStatus === "saving" ? (
            <svg width="16" height="16" viewBox="0 0 24 24" style={{ animation: "editor-spin 0.8s linear infinite" }}>
              <circle cx="12" cy="12" r="10" stroke="rgba(0,0,0,0.2)" strokeWidth="3" fill="none" />
              <path d="M12 2a10 10 0 0 1 10 10" stroke="#000" strokeWidth="3" strokeLinecap="round" fill="none" />
            </svg>
          ) : (
            <Icon id="save" size={16}/>
          )}
          {saveStatus==="saving" ? "Saving..." : "Save"}
        </button>
        <div style={{ width: 1, height: 16, background: "rgba(255,255,255,0.1)", margin: "0 2px" }} />
        <Tooltip title="Keyboard Shortcuts" placement="bottom" color="rgba(15, 15, 18, 0.97)" mouseEnterDelay={0.1}>
          <button onClick={() => setShowHelpModal(true)} style={{...rawBtnStyle, width: 32, padding: 0, border: "none", color: "#000", background: "#ffcc00", transition: "all 0.15s"}} onMouseEnter={e => { e.currentTarget.style.transform="scale(1.05)"; }} onMouseLeave={e => { e.currentTarget.style.transform="scale(1)"; }}>
            ?
          </button>
        </Tooltip>
        {typeof onClose === "function" && (
          <Tooltip title="Close" placement="bottom" color="rgba(15, 15, 18, 0.97)" mouseEnterDelay={0.1}>
            <button onClick={onClose} style={{...rawBtnStyle, width: 32, padding: 0, border: "none", color: "#fff", background: "#ef4444", transition: "all 0.15s"}} onMouseEnter={e => { e.currentTarget.style.transform="scale(1.05)"; }} onMouseLeave={e => { e.currentTarget.style.transform="scale(1)"; }}>
              <Icon id="close" size={14}/>
            </button>
          </Tooltip>
        )}
      </div>
    </div>
  );
}

